import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Button, Divider, Grid, Paper, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Parser, ProcessNodeDefinitions } from 'html-to-react';

import { LabeledField } from 'components/labeled-field/labeled-field';

import { GeneralSearchItem } from './types';
import { generalSearchOptions } from './search-utils';
import { GeneralSearchEntityType, GeneralSearchTypeToPath } from 'enums';

const useStyles = makeStyles(() => ({
  container: {
    padding: '1rem',
  },
  titleRow: {
    marginBottom: '0.5rem',
  },
  button: {
    textTransform: 'none',
  },
  divider: {
    margin: '0.5rem 0',
  },
  emphesised: {
    fontWeight: 700,
  },
}));

type SearchResultProps = {
  item: GeneralSearchItem;
};

const parser = new Parser();

const isValidNode = (node: any) => {
  console.log(node);
  return true;
};

const processNodeDefinitions = new ProcessNodeDefinitions(React);
const parsingInstructions = [
  {
    shouldProcessNode: (node: Record<string, any>) => node?.name === 'em',
    processNode: (_node: Record<string, any>, children: any) => (
      <span style={{ fontWeight: 600 }}>{children}</span>
    ),
  },
  {
    shouldProcessNode: () => true,
    processNode: processNodeDefinitions.processDefaultNode,
  },
];

const parseHtml = (html: string) =>
  parser.parseWithInstructions(html, isValidNode, parsingInstructions);

const getItemLink = (item: GeneralSearchItem): string => {
  const baseUrl = GeneralSearchTypeToPath[item.archivalEntityType];
  const isEad = item.archivalEntityType === GeneralSearchEntityType.EAD_NODE;
  const isAu =
    item.archivalEntityType === GeneralSearchEntityType.ARCHIVAL_UNIT;

  const isOriginator =
    item.archivalEntityType === GeneralSearchEntityType.ORIGINATOR;
  const level = isEad ? 2 : 0;
  const suffix = isEad ? '/ead' : '';
  if (isAu) {
    return `/${baseUrl}/${item.id}`;
  }
  if (isOriginator) {
    return `/${baseUrl}/${item.id}`;
  }
  return `/${baseUrl}/${level}/${item.id}${suffix}`;
};

export const SearchResult = ({ item }: SearchResultProps) => {
  const { label, archivalEntityType, timeRange, hitFields, created } = item;
  const styles = useStyles();

  const type = useMemo(
    () =>
      generalSearchOptions.find(({ id }) => id === archivalEntityType)?.label,
    [archivalEntityType]
  );

  const createdDate = useMemo(
    () => (created ? format(new Date(created), 'dd.MM.yyyy hh:ss') : undefined),
    [created]
  );

  return (
    <Grid
      container
      component={Paper}
      elevation={0}
      className={styles.container}
    >
      <Grid item xs={12} className={styles.titleRow}>
        <Button
          component={Link}
          to={getItemLink(item)}
          className={styles.button}
          target="_blank"
        >
          <Typography variant="h6" component="span">
            {label}
          </Typography>
        </Button>
      </Grid>
      <Grid item md={6} xs={12}>
        <LabeledField label="Typ" content={type ?? 'Neznámý typ'} />
      </Grid>
      <Grid item md={6} xs={12}>
        <LabeledField label="Datum vytvoření" content={createdDate ?? '-'} />
      </Grid>
      <Grid item md={6} xs={12}>
        <LabeledField label="Časový rozsah" content={timeRange} />
      </Grid>
      <Grid item xs={12} className={styles.divider}>
        <Divider variant="fullWidth" />
      </Grid>
      {Object.keys(hitFields)
        .filter((key) => key !== 'archivalEntityType')
        .map((key) => (
          <Grid item xs={12}>
            <Typography>
              <b>{key}: </b>
              {parseHtml(hitFields[key])}
            </Typography>
          </Grid>
        ))}
    </Grid>
  );
};
